// This is a COPY of downread/shared/src/utils/feed-privacy.ts. To modify, edit the original and then run downread/bin/build-shared.sh
/**
 * Detects whether a feed URL is "obviously personal" and must therefore be added as a private
 * feed only. The same logic is enforced on the frontend (to hide the public button) and on the
 * backend (to reject mode='public').
 *
 * See the plan / docs for the full rule spec. In short:
 * - Whitelisted hosts are always allowed as public.
 * - Otherwise, a non-empty query string flags the feed unless the query string is "feed-shaped"
 *   (contains rss/atom/json tokens or innocuous params like lang/site/page/format/type/cm*).
 */

const WHITELIST_DOMAINS = [
    'fortune.com',
    'wiley.com',
    'technoblogy.com',
    'feedspot.com',
    'youtube.com',
    'dailycaller.com',
];

// Innocuous param names that select content/format but never identify a user. 'input' is a
// content-selector used by some CMSes (e.g. ehstoday.com's website-scheduled-content.xml?input=…).
// 'output'/'format'/'alt'/'rss' select the feed format; 'title'/'hostname' name the source, not a
// user. Names starting with 'sort' (sort, sort_direction) and 'cm' are handled in isParamAllowed.
const ALLOWED_PARAM_NAMES = new Set([
    'lang', 'site', 'page', 'format', 'type', 'input', 'output', 'alt', 'rss', 'title', 'hostname',
]);
const ALLOWED_PARAM_VALUES = new Set(['us', 'en', 'en-us', 'en-ca']);
const FEED_TOKENS = ['rss', 'atom', 'json'];

function isWhitelistedHost(hostname: string): boolean {
    const host = hostname.toLowerCase();
    for (const domain of WHITELIST_DOMAINS) {
        if (host == domain || host.endsWith('.' + domain))
            return true;
    }
    return false;
}

function isRedditHost(hostname: string): boolean {
    const host = hostname.toLowerCase();
    return host == 'reddit.com' || host.endsWith('.reddit.com');
}

/**
 * Google News (news.google.com) feeds are personalized topic/search aggregations rather than a
 * single source site, so they are always private-only and are also skipped by the auto-add and
 * all.txt bulk importers (see createAndProcessFeed).
 */
export function isNewsGoogleUrl(url: string): boolean {
    try {
        return new URL(url).hostname.toLowerCase() == 'news.google.com';
    } catch {
        return false;
    }
}

/** User-facing message shown when a non-admin tries to add a reddit feed. */
export const REDDIT_UNSUPPORTED_MESSAGE = 'Reddit has limited its RSS feeds, so we can no longer support adding Reddit feeds.';

/**
 * True when `url` points at reddit.com (or any subdomain). Reddit now rate-limits RSS to
 * roughly one request per minute, which we can't poll at scale, so reddit feeds can no
 * longer be added by normal users (admins may still add them).
 */
export function isRedditUrl(url: string): boolean {
    try {
        return isRedditHost(new URL(url).hostname);
    } catch {
        return false;
    }
}

/**
 * Reddit paths that may be public: the site feed (/.rss) and a single subreddit's
 * default/new/hot feed (/r/<sub>/.rss, /r/<sub>/new.rss, /r/<sub>/hot.rss). The
 * subreddit name may not contain '+' (that joins multiple subreddits, which is a
 * personalized combination). Everything else on reddit.com (comment feeds, search
 * feeds, user feeds, multi-subreddit feeds, …) is private-only.
 */
const REDDIT_PUBLIC_PATH = /^\/(?:\.rss|r\/[A-Za-z0-9_]+\/(?:new|hot)?\.rss)$/;

function isRefParam(name: string): boolean {
    const lower = name.toLowerCase();
    return lower.startsWith('ref') || lower.endsWith('ref') || lower.startsWith('utm_') || lower.startsWith('fbref');
}

function isParamAllowed(name: string, value: string): boolean {
    const nameLower = name.toLowerCase();
    const valueLower = value.toLowerCase();

    // Referrer-style params (ref, referrer, ref_src, fbref, …) never personalize the feed.
    if (isRefParam(name))
        return true;

    // Param name OR value contains rss/atom/json AND value (if any) is <= 10 chars
    for (const tok of FEED_TOKENS) {
        if ((nameLower.includes(tok) || valueLower.includes(tok)) && value.length <= 10)
            
            return true;
    }

    // Known-innocuous param names, plus sort selectors (sort, sort_direction) and cm* tracking.
    if (ALLOWED_PARAM_NAMES.has(nameLower) || nameLower.startsWith('cm') || nameLower.startsWith('sort'))
        return true;

    if (ALLOWED_PARAM_VALUES.has(valueLower))
        return true;

    // Value shapes that cannot personalize a feed regardless of the param name:
    //   empty value (e.g. ?OpenView=), a lone '*' wildcard (e.g. ?query=%2A → '*'), or a very short
    //   number (e.g. ?id=1, ?rpp=10 — pagination/section ids, not opaque per-user tokens).
    if (value == '' || value == '*' || /^\d{1,4}$/.test(value))
        return true;

    return false;
}

/**
 * Strip referrer-style query parameters (name starts or ends with 'ref') from a URL.
 * If the only params were ref-style, the query string is removed entirely.
 * Returns the input unchanged on parse failure or when no ref params are present.
 */
export function stripRefParams(url: string): string {
    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        return url;
    }

    const namesToDelete = Array.from(parsed.searchParams.keys()).filter(isRefParam);
    if (!namesToDelete.length) return url;

    for (const name of namesToDelete)
        parsed.searchParams.delete(name);

    return parsed.toString();
}

/**
 * RSS tunnel / proxy services (openrss.org, politepaul.com, rss.app, …).
 *
 * These repackage a third-party site's content as a personalized feed for a
 * single user's view of it, so tunnelled feeds must not be redistributed
 * publicly via Downread. The same check also skips favicon localization (the
 * tunnel host doesn't represent the underlying source site).
 *
 * `excludePaths` lists paths that are the service's *own* content (e.g. the
 * openrss.org blog) and are therefore NOT tunnelled. A path is excluded when
 * it equals an entry exactly or is nested under it (entry + '/...').
 */
interface RssTunnel {
    domain: string;
    excludePaths?: string[];
}

const RSS_TUNNELS: RssTunnel[] = [
    { domain: 'openrss.org', excludePaths: ['/blog.rss', '/blog'] },
    { domain: 'politepaul.com' },
    { domain: 'rss.app' },
    { domain: 'newsloth.com' },
    { domain: 'fetchrss.com' },
    { domain: 'rsseverything.com' },
    { domain: 'sponder.app' },
    { domain: 'morss.it' },
];

function isExcludedTunnelPath(path: string, excludePaths?: string[]): boolean {
    return !!excludePaths?.some(ex => path == ex || path.startsWith(ex + '/'));
}

/**
 * If `url` is an RSS tunnel feed, return the matched tunnel domain (e.g.
 * 'openrss.org'); otherwise undefined. Paths listed in the tunnel's
 * `excludePaths` (the service's own content) are treated as not tunnelled.
 */
export function getRssTunnelDomain(url: string): string | undefined {
    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        return undefined;
    }
    const host = parsed.hostname.toLowerCase();
    const tunnel = RSS_TUNNELS.find(t => host == t.domain || host.endsWith('.' + t.domain));
    if (!tunnel || isExcludedTunnelPath(parsed.pathname, tunnel.excludePaths))
        return undefined;
    return tunnel.domain;
}

/**
 * True for feeds tunnelled through any known RSS tunnel/proxy service.
 */
export function isRssTunnelledUrl(url: string): boolean {
    return getRssTunnelDomain(url) != null;
}

export function isPrivateOnlyUrl(url: string): boolean {
    if (isRssTunnelledUrl(url))
        return true;

    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        return false;
    }

    // Google News feeds are personalized topic/search aggregations — always private.
    if (parsed.hostname.toLowerCase() == 'news.google.com')
        return true;

    // Paths that contain 'subscriber' (e.g. paid-subscriber-only feeds) are personalized.
    if (/subscriber/i.test(parsed.pathname))
        return true;

    // Reddit: only the site feed and a single subreddit's default/new/hot feed may be public.
    // Any other path is private-only. Allowed paths still fall through to the query-string
    // checks below, so personalized variants (e.g. ?feed=<key>&user=<name>) stay private.
    if (isRedditHost(parsed.hostname) && !REDDIT_PUBLIC_PATH.test(parsed.pathname))
        return true;

    if (isWhitelistedHost(parsed.hostname))
        return false;

    // Strip leading '?'. An empty (or '?'-only) query string is fine — treat as no query.
    const qs = parsed.search.replace(/^\?/, '');
    if (!qs)
        return false;

    // Non-parameter form: allow only if it contains 'rss' (case-insensitive).
    if (!qs.includes('=') && !qs.includes('&'))
        return !/rss/i.test(qs);

    // Parameter form: every parameter must be in the allow list.
    // (Array.from + tuple loop avoids the downlevelIteration constraint on URLSearchParams in some build targets.)
    for (const entry of Array.from(parsed.searchParams)) {
        if (!isParamAllowed(entry[0], entry[1]))
            return true;
    }
    return false;
}

// Feed titles of comment feeds, e.g. WordPress' per-post "Comments on: <post>" and site-wide
// "Comments for <site>" (plus the common translations of both).
const COMMENT_FEED_TITLE = /^\s*(comments? (on|for)\b|kommentare (zu|für|fuer)\b|commentaires (sur|pour|de)\b|comentarios (en|sobre|para|de)\b|commenti (su|per)\b|reacties op\b)/i;

/**
 * True for a feed of comments rather than posts: its title marks it as one, or every item links to
 * a comment anchor (…/#comment-123). Comment feeds are only allowed as private feeds — a public
 * one would publish strangers' comments as if they were posts.
 */
export function isCommentFeed(title: string | undefined, itemLinks: (string | undefined)[]): boolean {
    if (title && COMMENT_FEED_TITLE.test(title))
        return true;
    return !!itemLinks.length && itemLinks.every(link => !!link && /#comment/i.test(link));
}
